/** The build order, the recruiting, and getting the army into one hand.
 *
 * The shape of a classic opening is not a search problem, it is a known order, and
 * pretending otherwise buys nothing: the hall chain pays for everything else, so
 * it goes first; the fort chain gates the upper dwellings and adds growth, so it
 * goes second; dwellings then rank by what they actually put on a battlefield -
 * `aiValue` times weekly growth - and the mage guild follows them.
 *
 * Every option scored here came out of `enumerateLegalCommands`, so an
 * unaffordable building or an over-large recruit is not filtered out - it was
 * never offered (3.3).
 */

import {
  HERO_HIRE_COST, RESOURCES, asCreatureDefId, caravanDays, entityKeys, executeCommand, freeSlotFor,
  learnableGuildSpells, makeCtx, payNum, payloadOf, recruitCost, townUpgradeOffers, tradeYield,
} from "@game/core";
import type {
  ArmyId, ArmySlot, BuildingDefId, CreatureDefId, LegalCommand, Resources, SpellDefId, TownId,
  TownState, HeroDefId, ResourceKind,
} from "@game/core";
import type { Shadow } from "./shadow.js";
import type { DifficultyProfile } from "./difficulty.js";
import type { Candidate } from "./types.js";
import {
  NEUTRAL_BP, SHORTAGE_BP, SHRINE_BASE, SHRINE_KNOWN, SHRINE_LOCKED, SHRINE_PER_LEVEL,
  resourceShortage, resourceWant, spellAppetite,
} from "./adventure.js";
import { MINE_HORIZON_DAYS, TIER_WEIGHT_BP_PER_TIER, goldEquivalent } from "./evaluate.js";
import { artifactWorth } from "./gear.js";
import { caravansByTown, mainHero, threatenedTownIds } from "./strategy.js";

/**
 * Slot priority. The gaps are wide because within a slot the winner is decided
 * by the slot's own metric, and a dwelling must never out-rank the hall chain
 * however good its creature is.
 */
export const SLOT_PRIORITY: Readonly<Record<string, number>> = Object.freeze({
  // Above the hall, and it is not close. The Grail structure costs nothing, it
  // is a victory condition on its own (`build_grail`), and it is legal for
  // exactly as long as the hero who dug it up is standing in the town - so a
  // turn spent buying anything else is a turn the bearer might walk away.
  grail: 12_000_000,
  hall: 10_000_000,
  fort: 6_000_000,
  dwelling_7: 4_000_000,
  dwelling_6: 4_000_000,
  dwelling_5: 4_000_000,
  dwelling_4: 4_000_000,
  dwelling_3: 4_000_000,
  dwelling_2: 4_000_000,
  dwelling_1: 4_000_000,
  mage_guild: 3_000_000,
  marketplace: 2_800_000,
  resource_silo: 2_500_000,
  blacksmith: 1_600_000,
  tavern: 1_500_000,
});
export const SLOT_PRIORITY_DEFAULT = 1_000_000;
/** A trade that unblocks a building: just under the building it unblocks. */
export const TRADE_UNBLOCK = 500_000;

/** Ceiling on what a hero's sheet adds to a hire, so it never crosses a tier. */
const HIRE_WORTH_CAP = 100_000;

/**
 * What a hero is worth on the day he is hired: his four primaries, his
 * starting skills, a spell in the book, and a specialty that pays every day.
 * A resource specialty is priced like a mine - the daily yield over the
 * horizon the mine scorer uses - which is how a +1 crystal hero out-bids a
 * hero with one more point of attack.
 */
function heroDefWorth(sh: Shadow, def: HeroDefId): number {
  const hd = sh.content.heroes.get(def);
  if (hd === undefined) return 0;
  const p = hd.startingPrimary;
  let worth = (p.attack + p.defense + p.spellPower + p.knowledge) * 300;
  for (const s of hd.startingSkills) worth += s.level * 400;
  if (hd.startingSpell !== null) worth += 500;
  for (const m of hd.specialty.modifiers) {
    const stat = m.stat as string;
    if (!stat.startsWith("income.") || m.op !== "add") continue;
    const r = stat.slice("income.".length) as ResourceKind;
    worth += m.value * (sh.content.resourceValue[r] ?? 1) * MINE_HORIZON_DAYS;
  }
  return Math.min(HIRE_WORTH_CAP, worth);
}

const slotScore = (slot: string): number => SLOT_PRIORITY[slot] ?? SLOT_PRIORITY_DEFAULT;

/** What a dwelling is worth: a week of its creature, priced in `aiValue`. */
function dwellingWorth(sh: Shadow, creature: CreatureDefId | null): number {
  if (creature === null) return 0;
  const def = sh.content.creatures.get(creature);
  if (def === undefined) return 0;
  return def.aiValue * (def.growth > 0 ? def.growth : 1);
}

/**
 * Gold the AI declines to spend on creatures because a building wants it.
 *
 * The target is the next building in line for the town, not a fraction of the
 * purse: "keep four thousand because the Citadel costs four thousand" is a
 * decision a designer can check, and "keep 40% of whatever you happen to hold"
 * is not.
 */
export function buildReserve(sh: Shadow, t: TownState, p: DifficultyProfile): number {
  if (p.buildReserveBp <= 0) return 0;
  const built = new Set<string>(t.built as readonly string[]);
  let best = 0, bestScore = 0;
  for (const b of sh.content.buildings.values()) {
    if ((b.faction as string) !== (t.faction as string)) continue;
    if (built.has(b.id as string)) continue;
    let ready = true;
    for (const r of b.requires) if (!built.has(r as string)) { ready = false; break; }
    if (!ready) continue;
    const score = slotScore(b.slot);
    if (score > bestScore) { bestScore = score; best = b.cost.gold ?? 0; }
  }
  return Math.floor((best * p.buildReserveBp) / 10000);
}

// ------------------------------------------------------------------ scoring

/**
 * A guild is worth building when somebody can read it.
 *
 * A Mage Guild teaches spells up to its own level, and a hero whose Wisdom caps
 * it at level two learns nothing from a level-four guild - the gold buys a room
 * full of books nobody in the army can open. So the guild floats: a level the
 * heroes can actually use outranks the next dwelling, and a level above their
 * Wisdom sinks below the marketplace and waits there until Wisdom arrives. The
 * measurement is `maxSpellLevel`, the kernel's own learning gate, so the AI's
 * appetite and the rule that will refuse the spell cannot drift apart.
 */
/**
 * Above every build and every recruit, because it is the command without which
 * none of them matter. See the `HIRE_HERO` case in `planTown`.
 */
export const HIRE_LAST_HERO = 100_000_000;

export const GUILD_USABLE_BONUS = 1_500_000;
export const GUILD_UNUSABLE_PENALTY = 1_500_000;

function scoreBuild(sh: Shadow, town: TownState, id: BuildingDefId): Candidate | null {
  const def = sh.content.buildings.get(id);
  if (def === undefined) return null;
  let score = slotScore(def.slot);
  if (def.dwellingFor !== null) score += dwellingWorth(sh, def.dwellingFor);
  if (def.guildLevel > 0) {
    score += def.guildLevel <= spellAppetite(sh).usableLevel
      ? GUILD_USABLE_BONUS + def.guildLevel * 1000
      : -GUILD_UNUSABLE_PENALTY;
  }
  // Within a tier, cheaper first: two equal-priority buildings should be bought
  // in the order that leaves the most gold for the next one.
  score -= Math.floor(goldEquivalent(sh.content, def.cost) / 100);
  void town;
  return { command: { type: "BUILD", town: town.id, building: id }, score, why: `build.${def.slot}` };
}

/**
 * Do not buy an army you cannot pick up again.
 *
 * `recruitTargetArmy` puts the week's recruits into the hero standing in the
 * town if there is one, and into the hall's own army if there is not - and the
 * hall's army is only movable while the kingdom owns it. A town taken from
 * somebody else keeps the army object it was captured with, so a hero can walk
 * into a conquered hall, see three hundred thousand points of creature standing
 * in it, and have no legal command that touches any of it. Measured on seed
 * 12161 at day 200: `town:5`, `town:6` and `town:7` had all changed hands, all
 * three garrison armies still read `owner: null`, and 1.09M of creature bought
 * with the winner's own gold was unreachable for the rest of the game.
 *
 * That is a kernel matter and it is reported as one. What belongs here is the
 * question the AI should be asking anyway, bug or no bug: gold spent on a stack
 * that cannot join the war is gold burnt, so buy where the hero is, or wait for
 * him. The rule reads the same and costs nothing once the hall is ours.
 */
function recruitsCanBeCollected(sh: Shadow, t: TownState): boolean {
  if (t.visitingHero !== null || t.garrisonHero !== null) return true;
  return (sh.state.armies[t.garrison]?.owner ?? null) === sh.player;
}

/** Troops bought into the hand that is going to fight with them, not the hall. */
export const RECRUIT_IN_HAND_BP = 15000;

function scoreRecruit(
  sh: Shadow, town: TownState, creature: CreatureDefId, count: number, reserve: number,
): Candidate | null {
  const def = sh.content.creatures.get(creature);
  if (def === undefined || count <= 0) return null;
  if (!recruitsCanBeCollected(sh, town)) return null;
  const cost = recruitCost(sh.content, creature, count) as Partial<Resources>;
  const gold = cost.gold ?? 0;
  const purse = sh.obs.resources?.gold ?? 0;
  // A kingdom with nobody on the board keeps a hero's fare back whatever else
  // it wants, because a stack it cannot carry is a stack it does not have.
  const floor = (sh.obs.self?.heroes.length ?? 0) === 0
    ? Math.max(reserve, HERO_HIRE_COST) : reserve;
  if (purse - gold < floor) return null;
  // Value bought, less a nudge toward the higher tier when two buys price the
  // same: a slot spent on a tier-one stack is a slot not spent on a tier-six.
  let score = def.aiValue * count + def.tier * 500;
  if (town.visitingHero !== null || town.garrisonHero !== null) {
    score = Math.floor((score * RECRUIT_IN_HAND_BP) / 10000);
  }
  return {
    command: { type: "RECRUIT", town: town.id, creature, count },
    score,
    why: `recruit.t${def.tier}`,
  };
}

/**
 * How much a column loses per day on the road, in basis points of its value.
 *
 * Six hundred is a day and a half of a creature's own weekly growth, which is
 * roughly what waiting costs: a stack that will not be there for a week is
 * worth about what next week's dwelling output is worth, and a stack that
 * arrives tomorrow is worth almost what it is worth today. It is a discount and
 * never a veto - the floor below keeps a very long haul at a fifth of face
 * value rather than at nothing, because a dwelling whose stock is otherwise
 * unspendable is worth reaching for even slowly.
 */
export const CARAVAN_DAY_DISCOUNT_BP = 600;
export const CARAVAN_FLOOR_BP = 2000;

/**
 * How much of a column's face value survives when one is already walking to the
 * same hall, per column already on the road.
 *
 * Reinforcements pile up in one garrison and only one hero can carry them out,
 * so the fourth column to a quiet town is worth much less than the first: it is
 * the same troops arriving in the same place, and what the kingdom is short of
 * by then is a hand to hold them, not more stock. Halving per column already
 * inbound leaves the first at face, the second at half and the fourth at an
 * eighth, which is enough to make the planner spread its buying across towns
 * without ever refusing a column outright.
 *
 * This became possible to ask at all when the observation started carrying
 * caravans; before that every column looked like the first one.
 */
export const CARAVAN_CROWD_BP = 5000;

function scoreCaravan(
  sh: Shadow, dest: TownState, c: Extract<LegalCommand, { type: "SEND_CARAVAN" }>,
  reserve: number, inbound: number,
): Candidate | null {
  const def = sh.content.creatures.get(c.creature);
  if (def === undefined || c.count <= 0) return null;
  const cost = recruitCost(sh.content, c.creature, c.count) as Partial<Resources>;
  const gold = cost.gold ?? 0;
  const purse = sh.obs.resources?.gold ?? 0;
  if (purse - gold < reserve) return null;

  const from = c.fromTown !== null
    ? sh.state.towns[c.fromTown] ?? null
    : c.fromObject !== null ? sh.state.objects[c.fromObject] ?? null : null;
  if (from === null) return null;
  const days = caravanDays(from, dest);

  const face = def.aiValue * c.count + def.tier * 500;
  const keepBp = Math.max(
    CARAVAN_FLOOR_BP, 10000 - Math.min(10000, days * CARAVAN_DAY_DISCOUNT_BP),
  );
  let score = Math.floor((face * keepBp) / 10000);
  // Capped at six halvings: beyond that the score is zero anyway and the loop
  // is a way to make a hot path depend on how many caravans exist.
  for (let i = 0; i < Math.min(inbound, 6); i++) {
    score = Math.floor((score * CARAVAN_CROWD_BP) / 10000);
  }
  return {
    command: c,
    score,
    why: `caravan.t${def.tier}.d${days}.q${inbound}`,
  };
}

// -------------------------------------------------------------------- plan

/**
 * Score the town half of one legal-command list: building, recruiting, hiring,
 * and folding a garrison into the hero who is standing in the town.
 */
/**
 * The spells a guild slot is worth trading for, best first.
 *
 * Two spells of one level are priced the same by the shrine currency, so
 * without this a research that swaps Arcane Dart for Slow is worth exactly
 * nothing and is never bought. The list is the classic consensus on which spells
 * decide games - tempo, mobility and a second army - and it is a hash-free,
 * random-free preference exactly like `SKILL_PREFERENCE` in the controller: a
 * ranking that is wrong in a fixed direction beats one that moves.
 */
export const SPELL_PREFERENCE: readonly string[] = Object.freeze([
  "spell.slow", "spell.haste", "spell.homeward_gate", "spell.blink_step",
  "spell.resurrection", "spell.crush", "spell.forked_lightning", "spell.skyward",
  "spell.raise_fallen", "spell.benediction", "spell.clone", "spell.blood_rage",
  "spell.starfall", "spell.blind", "spell.riposte", "spell.bless",
  "spell.shield", "spell.granite_hide", "spell.cure", "spell.teleport",
]);

/** Gold a place at the top of `SPELL_PREFERENCE` is worth; the bottom is zero. */
export const SPELL_PREFERENCE_GOLD = 4000;

/**
 * What one spell is worth to this kingdom, in the shrine currency.
 *
 * The same three tiers `objectValue` prices a shrine at - known by every hero,
 * above every hero's Wisdom, or usable - because a guild slot and a shrine are
 * the same thing to a hero walking in: one spell, learned once. The preference
 * bonus on top is what separates two spells of one level.
 */
export function spellWorth(sh: Shadow, spell: SpellDefId): number {
  const def = sh.content.spells.get(spell);
  if (def === undefined) return 0;
  const app = spellAppetite(sh);
  if (app.knownByAll.has(spell as string)) return SHRINE_KNOWN;
  if (def.level > app.usableLevel) return SHRINE_LOCKED;
  const rank = SPELL_PREFERENCE.indexOf(spell as string);
  const pref = rank < 0 ? 0
    : Math.floor((SPELL_PREFERENCE_GOLD * (SPELL_PREFERENCE.length - rank)) / SPELL_PREFERENCE.length);
  return SHRINE_BASE + def.level * SHRINE_PER_LEVEL + pref;
}

/** Score tag for a research, so a balance report can tell the levels apart. */
const researchWhy = (level: number): string => `research.l${level}`;

/**
 * Four of the forty-eight command kinds are deliberately never scored here,
 * and each is a decision rather than a gap:
 *
 * - `TOGGLE_AUTO_BUILD` hands the build order to the kernel's "first
 *   affordable structure" rule. `scoreBuild` is the AI's build order and is
 *   better than that by construction; switching it on would only ever build
 *   something `scoreBuild` declined.
 * - `DISMISS_HERO` frees nothing. Heroes carry no upkeep in this kernel (the
 *   endgame charge in `turn.ts` is on army value, and a dismissed hero's army
 *   is dropped with him), so a hero costs nothing to keep, and the one thing a
 *   dismissal buys - a roster slot at the cap of eight - is spent on a 2500
 *   gold level-one replacement the planner cannot show is better than the
 *   level-one it just fired. Conservative means never, until there is a
 *   measurement that says otherwise.
 * - `SPLIT_STACK` is the other half of `MERGE_STACK`, which `consolidate`
 *   scores at a flat 400 on the rule that two stacks of one creature are
 *   strictly worse than one. Scoring a split as well would have the two
 *   phases undo each other every turn. The classic tactic it exists for - a
 *   one-creature stack to absorb a retaliation or block a shooter - needs a
 *   battle placer that knows what to do with the chaff, and `battle.ts` has
 *   no such term; a split it cannot use is a stack it cannot use.
 * - `SWAP_STACK` reorders slots, and nothing this planner or `battle.ts`
 *   measures moves with slot order, so every swap scores the same as not
 *   swapping. A command that cannot change a score is a command spent.
 */
export function planTown(
  sh: Shadow, p: DifficultyProfile, legal: readonly LegalCommand[],
): Candidate[] {
  const out: Candidate[] = [];
  const reserves = new Map<string, number>();
  const inbound = caravansByTown(sh);
  const townOf = (id: TownId): TownState | undefined => sh.state.towns[id];

  for (const id of entityKeys(sh.state.towns)) {
    const t = townOf(id);
    if (t !== undefined) reserves.set(id as string, buildReserve(sh, t, p));
  }
  // The largest reserve anywhere, for a purchase that belongs to no town.
  const anyReserve = Math.max(0, ...reserves.values());
  // Towns with a hire on offer right now: a hero stepping into one of them
  // blocks the Tavern until he leaves, and a hire is worth more than a step.
  const hiring = new Set<string>();
  for (const c of legal) if (c.type === "HIRE_HERO") hiring.add(c.town as string);
  let ctx: ReturnType<typeof makeCtx> | null = null;
  const ctxOf = (): ReturnType<typeof makeCtx> => {
    if (ctx === null) ctx = makeCtx(sh.state, sh.content);
    return ctx;
  };

  for (const c of legal) {
    switch (c.type) {
      case "BUILD": {
        const t = townOf(c.town);
        if (t === undefined) break;
        const cand = scoreBuild(sh, t, c.building);
        if (cand !== null) out.push(cand);
        break;
      }
      case "RECRUIT": {
        const t = townOf(c.town);
        if (t === undefined) break;
        const cand = scoreRecruit(sh, t, c.creature, c.count, reserves.get(c.town as string) ?? 0);
        if (cand !== null) out.push(cand);
        break;
      }
      /**
       * A caravan, priced as the recruit it is minus what the wait costs.
       *
       * The value is the creatures bought, exactly as `scoreRecruit` prices
       * them, discounted by the days they spend on the road - a stack that
       * arrives in eight days is worth materially less than the same stack in
       * hand today, and without that discount the AI would empty every purse
       * into columns from the far corner of the map on turn one.
       *
       * It is scored at all because the alternative is worse than a bad score:
       * an owned external dwelling with no caravan is stock that accumulates
       * for the whole game and is never spent, which is the exact behaviour
       * caravans were added to end.
       */
      case "SEND_CARAVAN": {
        const t = townOf(c.town);
        if (t === undefined) break;
        const cand = scoreCaravan(
          sh, t, c, reserves.get(c.town as string) ?? 0,
          (inbound.get(c.town as string) ?? []).length);
        if (cand !== null) out.push(cand);
        break;
      }
      case "HIRE_HERO": {
        // A second hero doubles the map coverage - and, more to the point, it
        // is what lets the first one leave: a kingdom with one hero either
        // marches with an empty capital behind it or never marches at all. How
        // many after that is a difficulty knob, because paying for a fourth
        // hero is a judgement rather than an opening.
        //
        // With *no* hero it is not a judgement at all, and this is the case
        // that used to be lost among the others. A kingdom with no hero on the
        // board cannot take a step, cannot flag a mine, cannot pick its own
        // garrison up and cannot be eliminated either, because `townlessDays`
        // does not fire on a player who still holds his hall - so it sits
        // there until the day cap. Measured on 36x36 seed 40595: at day 201
        // *both* kingdoms held one town, no heroes, and 473k and 220k of army
        // in halls nobody could ever come and collect. It happens because a
        // recruit is scored at `aiValue * count`, which for a week of a good
        // dwelling is millions, so the purse went on creatures every morning
        // and the two and a half thousand gold for a hero was never there.
        const heroes = sh.obs.self?.heroes.length ?? 0;
        const max = p.maxHeroes;
        const tier = heroes === 0 ? HIRE_LAST_HERO
          : heroes < 2 ? 900_000 : heroes < max ? 200_000 : 0;
        // Which of the Tavern's offers, once the tier says "hire": the tier is
        // shared by every offer, so without this the pick fell to the sort's
        // tie-break and the kingdom always hired the alphabetically first
        // name. Measured: fifteen of fifteen hires on one seed.
        const score = tier > 0 ? tier + heroDefWorth(sh, c.hero) : 0;
        const purse = sh.obs.resources?.gold ?? 0;
        const t = townOf(c.town);
        // The reserve is gold held back for a building. A kingdom with nobody
        // to build for is not saving up, it is stuck. From the third hero on
        // a hire also has to leave a hire's worth behind for troops, or the
        // purse goes on commanders who march out alone and die: twelve hired
        // and twelve lost in forty days, on one seed, before this line.
        const reserve = heroes === 0 ? 0 : reserves.get(c.town as string) ?? 0;
        const floor = heroes >= 2 ? reserve + HERO_HIRE_COST : reserve;
        if (score > 0 && t !== undefined && purse - HERO_HIRE_COST >= floor) {
          out.push({ command: c, score, why: "hire" });
        }
        break;
      }
      // Section 27: a war machine off the Blacksmith's counter. Priced against
      // the machine's own aiValue rather than the artifact's gold, because what
      // it is worth is what it does on the field; the reserve still comes
      // first, so a kingdom saving for a dwelling does not spend it on a tent.
      case "TOWN_BUY_ARTIFACT": {
        const t = townOf(c.town);
        if (t === undefined) break;
        const art = sh.content.artifacts.get(c.artifact);
        if (art === undefined) break;
        const machine = sh.content.creatures.get(asCreatureDefId(`warmachine.${c.artifact as string}`));
        const purse = sh.obs.resources?.gold ?? 0;
        const reserve = reserves.get(c.town as string) ?? 0;
        if (purse - art.cost < reserve) break;
        const value = machine?.aiValue ?? Math.floor(art.cost / 2);
        out.push({ command: c, score: value, why: "war_machine" });
        break;
      }
      /**
       * A trade out of a shortage. Only when something ready to build is short
       * of `take` and the kingdom is not short of `give`; sized to close the
       * gap rather than the one-unit minimum the enumerator offers, and priced
       * just under a build, because that is what it is for. Measured before
       * this existed: a kingdom sat on 108 gems and 52 crystal while its fort
       * waited on ten wood, and never traded once in a thousand decisions.
       */
      case "TRADE_RESOURCE": {
        const have = sh.obs.resources;
        if (have === null) break;
        const short = resourceShortage(sh);
        if (short[c.take] !== SHORTAGE_BP || short[c.give] !== NEUTRAL_BP) break;
        const want = resourceWant(sh);
        const gap = (want[c.take] ?? 0) - have[c.take];
        if (gap <= 0) break;
        const ctx = makeCtx(sh.state, sh.content);
        // The smallest multiple of the enumerated amount that closes the gap,
        // or as much of it as the purse allows without going short ourselves.
        let amount = c.amount;
        while (tradeYield(ctx, c.player, c.give, c.take, amount) < gap && amount + c.amount <= have[c.give]) {
          amount += c.amount;
        }
        const keep = c.give === "gold" ? Math.max(...[...reserves.values()], 0) : 0;
        if (have[c.give] - amount < keep) break;
        const got = tradeYield(ctx, c.player, c.give, c.take, amount);
        if (got <= 0) break;
        out.push({ command: { ...c, amount }, score: TRADE_UNBLOCK, why: `trade.${c.give}>${c.take}` });
        break;
      }
      /**
       * An upgrade, in the recruit's currency: the value the stack gains, less
       * nothing, because a recruit is not charged for its gold either - the
       * reserve is the only brake on both. Never taken in three games of 471
       * legal offers before this case existed, so the human never met an
       * upgraded computer creature.
       */
      case "UPGRADE_UNITS": {
        const t = townOf(c.town);
        if (t === undefined) break;
        const offer = townUpgradeOffers(makeCtx(sh.state, sh.content), t, c.army)
          .find((o) => o.slot === c.slot);
        if (offer === undefined) break;
        const purse = sh.obs.resources?.gold ?? 0;
        const reserve = reserves.get(c.town as string) ?? 0;
        if (purse - (offer.cost.gold ?? 0) < reserve) break;
        const gain = (offer.to.aiValue - offer.from.aiValue) * offer.count;
        if (gain <= 0) break;
        out.push({ command: c, score: gain, why: `upgrade.t${offer.from.tier}` });
        break;
      }
      /**
       * The expansion research, priced as the trade it is: what the guild gains, less
       * what it drops, less the bill. The kernel names all three - the
       * command is run against the shadow and the `SpellResearched` event
       * and the purse delta are read back - so the AI never re-derives which
       * spell `pick` means or what a level costs. It only pays when the slot
       * it replaces teaches nobody anything (every hero already has it) or
       * the offer is a spell the preference list wants, and never out of a
       * rare a ready building is waiting on.
       */
      case "RESEARCH_SPELL": {
        const t = townOf(c.town);
        const before = sh.obs.resources;
        if (t === undefined || before === null) break;
        const r = executeCommand(sh.state, c, sh.content);
        if (!r.ok) break;
        let gained: SpellDefId | null = null, dropped: SpellDefId | null = null;
        for (const e of r.value.events) {
          if (e.type === "SpellResearched") { gained = e.gained; dropped = e.dropped; }
        }
        if (gained === null || dropped === null) break;
        const after = r.value.state.players[sh.player]?.resources;
        if (after === undefined) break;
        const short = resourceShortage(sh);
        let bill = 0, gold = 0, starved = false;
        for (const k of RESOURCES) {
          const paid = before[k] - after[k];
          if (paid <= 0) continue;
          if (k === "gold") gold = paid;
          else if (short[k] === SHORTAGE_BP) starved = true;
          bill += paid * (sh.content.resourceValue[k] ?? 1);
        }
        if (starved) break;
        if (before.gold - gold < (reserves.get(c.town as string) ?? 0)) break;
        const gain = spellWorth(sh, gained) - spellWorth(sh, dropped) - bill;
        if (gain <= 0) break;
        out.push({ command: c, score: gain, why: researchWhy(c.level) });
        break;
      }
      /**
       * An artifact off a map merchant's counter, worth what wearing it is
       * worth less the price, and only when the hero can wear it now: the
       * kernel bags what it cannot socket, and gold spent on a bagged trinket
       * is gold spent on nothing. No shipped object sells one today - the
       * Blacksmith is `TOWN_BUY_ARTIFACT` above - but a scenario or a
       * generator that puts a `price` and an `artifact` on an object gets
       * a buyer rather than a hero who walks past.
       */
      case "BUY_ARTIFACT": {
        const h = sh.state.heroes[c.hero];
        const o = sh.state.objects[c.object];
        const def = sh.content.artifacts.get(c.artifact);
        if (h === undefined || o === undefined || def === undefined) break;
        if (freeSlotFor(h, def) === null) break;
        const price = payNum(payloadOf(ctxOf(), o), "price", def.cost);
        const purse = sh.obs.resources?.gold ?? 0;
        if (purse - price < anyReserve) break;
        const gain = artifactWorth(def) - price;
        if (gain <= 0) break;
        out.push({ command: c, score: gain, why: `buy.${def.rank}` });
        break;
      }
      /**
       * The free step into an adjacent town. It costs no movement, so it is
       * worth exactly what being inside is worth: the guild's spells the hero
       * has not learned (he learns on arrival), and - once he has no movement
       * left to spend anyway - a place to stand where tomorrow's recruits go
       * into his army rather than the garrison. A hero already inside has
       * nothing to gain from stepping in again.
       */
      case "HERO_TO_TOWN": {
        const h = sh.state.heroes[c.hero];
        const t = townOf(c.town);
        if (h === undefined || t === undefined || h.inTown === c.town) break;
        if (hiring.has(c.town as string) && (sh.obs.self?.heroes.length ?? 0) < p.maxHeroes) break;
        let score = h.movementLeft <= 0 ? CONSOLIDATE_TIDY : 0;
        for (const spell of learnableGuildSpells(ctxOf(), t, h)) score += spellWorth(sh, spell);
        if (score <= 0) break;
        out.push({ command: c, score, why: "enter" });
        break;
      }
      default: break;
    }
  }

  out.push(...consolidate(sh, p, legal));
  out.sort((a, b) => b.score - a.score);
  return out;
}

/**
 * Put the army in one hand - and know when not to.
 *
 * Two stacks of the same creature in one army are strictly worse than one, so
 * merging is always worth a command. Emptying a garrison into a hero is the
 * other half of force concentration and is emphatically *not* always right:
 *
 * - Into the hero who already carries the most, it is the difference between
 *   one army worth marching with and four scouts. Scored above every walk, so
 *   the hero who came home for the week's growth leaves with it rather than
 *   wandering off and coming back for it a stack at a time. This used to score
 *   300 against movement scores in the thousands, which meant it happened only
 *   when there was nothing at all to do.
 * - Into anyone else it is scored where it used to be: a tidy-up.
 * - Out of a town an enemy hero is standing in front of it is a gift. The
 *   garrison is the town's defence and handing it to a hero who is about to
 *   walk away loses the town, so a threatened town keeps what it has.
 */
export const CONSOLIDATE_MAIN = 500_000;
export const CONSOLIDATE_TIDY = 300;
/**
 * Handing an army over is priced in the same currency a walk to it is, and
 * that is the whole reason this is a constant rather than a flat score.
 *
 * `planAdventure` scores a destination as value over distance times a thousand,
 * so a hero standing next to a million-point hoard is looking at a number in the
 * billions - and a flat five hundred thousand for the transfer that would
 * actually pick it up loses to it every single morning. Measured on seed 12161:
 * `town.collect` was chosen 377 times in 200 days and `garrison.out` not once.
 * The hero walked to the hall, could not out-score walking to the *other* hall,
 * and left again with nothing.
 *
 * Ten is `1000 / DISTANCE_BASE`: the value of a thing you are already standing
 * on. A stack in the hall you are in therefore beats the same stack a day's
 * ride away, which is the ordering the rest of the planner already uses.
 */
export const PICKUP_PER_VALUE = 10;
/**
 * Making room, and it is the rule without which none of the above happens.
 *
 * An army has seven slots and a kingdom fields more than seven kinds of
 * creature, so "hand the garrison to the hero" is legal only while the hero has
 * a slot free or a stack of the same creature - `TRANSFER_STACK` is enumerated
 * into a null or matching slot and nowhere else. A hero who filled his seven
 * slots early fills them with what he met early, which is the bottom of the
 * ladder, and from that morning on the kingdom's whole output is unreachable.
 *
 * Measured on seed 12161 at day 201: `hero:1` carried 62 obsidian rookstones,
 * 231 tinkerlings and 30 iron sentinels - seven slots, 455k - while the hall behind
 * him held 182 master windcallers, 90 serpent_dancer queens and 36 giants, 1090k, and not one
 * command in the game could move any of it. Both armies were full and no two
 * stacks matched, so the position was a deadlock in the literal sense.
 *
 * The way out is to trade up: dump the worst stack into the hall if the hall
 * has room, and dismiss it if it does not. Throwing away 231 tinkerlings to pick
 * up 90 serpent_dancer queens is the correct trade and a human makes it without thinking.
 * The margin stops it happening in the other direction, and stops two stacks of
 * nearly equal worth swapping places every morning for the rest of the game.
 */
export const TRADE_UP_BP = 12000;
export const CONSOLIDATE_TRADE = 450_000;

/**
 * The one stack a hall keeps when its hero marches out.
 *
 * Force concentration is right up to the point where it hands the enemy the
 * capital. Consolidation was measured working - and then measured losing games
 * on day two: on 36x36 seed 16838, `p2`'s opening hero emptied its own hall
 * into its army, walked 28 tiles on day one, and took `p1`'s capital on day
 * two, because `p1` had done exactly the same thing and left nothing behind.
 * Four of the ten-seed headline were over by day five. That is round 9's free
 * win arriving through the AI instead of through the generator.
 *
 * So the hall keeps its single most valuable stack, and the rule has three
 * properties worth having: a town is never a walk-in, the answer does not move
 * when it is recomputed (a fraction of "what is left" drains to nothing one
 * command at a time), and a hero garrisoned in the town suspends it, because a
 * hero standing in a hall *is* its defence.
 *
 * An absolute floor under the hall on top of this was tried and measured: it
 * halves the day-6 rushes on the 36x36 headline and takes the 48x48
 * four-player headline from `decided 8 of 10` to 3, because a hall that stops
 * handing troops over is a hoard again wherever the kept stack is not the bulk
 * of it. The stack is the rule; the floor is not.
 */
function keptStack(sh: Shadow, garrison: ArmyId): number {
  const a = sh.state.armies[garrison];
  if (a === undefined) return -1;
  let best = -1, bestValue = 0;
  for (let i = 0; i < a.slots.length; i++) {
    const v = slotValue(sh, a.slots[i]);
    if (v > bestValue) { bestValue = v; best = i; }
  }
  return best;
}

/** One stack, in the currency `armyStrength` reports the whole army in. */
function slotValue(sh: Shadow, s: ArmySlot | null | undefined): number {
  if (s === null || s === undefined || s.count <= 0) return 0;
  const def = sh.content.creatures.get(s.def);
  if (def === undefined) return 0;
  return Math.floor(
    (def.aiValue * s.count * (10000 + TIER_WEIGHT_BP_PER_TIER * def.tier)) / 10000,
  );
}

/**
 * The stack the main hero should give up to make room for a better one, and
 * where it can go. Null when he is not in a hall, when the hall holds nothing
 * better, or when he still has a slot free - in which case the plain pickup
 * above does the job and nothing has to be given up at all.
 */
interface TradeUp {
  readonly heroArmy: string;
  readonly townArmy: string;
  readonly slot: number;
  /** True when the hall is full too, so the only way to make room is to dismiss. */
  readonly mustDismiss: boolean;
  /** What the swap is worth: the stack gained, less the stack given up. */
  readonly gain: number;
}

/**
 * Making room is worth what it makes room for, less what it costs - and it must
 * sit below the plain pickup, so a hero who can simply take a stack takes it
 * rather than giving one up first.
 */
function tradeScore(t: TradeUp): number {
  return Math.max(CONSOLIDATE_TRADE, t.gain * PICKUP_PER_VALUE);
}

function tradeUp(sh: Shadow, main: string | null, skip: ReadonlySet<string>): TradeUp | null {
  if (main === null) return null;
  const hero = sh.state.heroes[main as never];
  if (hero === undefined) return null;
  const mine = sh.state.armies[hero.army];
  if (mine === undefined) return null;
  let filled = 0;
  for (const s of mine.slots) if (s !== null && s.count > 0) filled++;
  // A free slot needs no trade, and a hero down to one stack may not give it up.
  if (filled < mine.slots.length || filled <= 1) return null;

  for (const id of entityKeys(sh.state.towns)) {
    const t = sh.state.towns[id];
    if (t === undefined || t.visitingHero !== hero.id) continue;
    if (skip.has(t.id as string)) continue;
    const gar = sh.state.armies[t.garrison];
    if (gar === undefined) continue;

    const kept = t.garrisonHero === null ? keptStack(sh, t.garrison) : -1;
    let bestHall = 0;
    let hallFull = true;
    for (let i = 0; i < gar.slots.length; i++) {
      const s = gar.slots[i];
      if (s === null || s === undefined || s.count <= 0) { hallFull = false; continue; }
      if (i === kept) continue;
      const v = slotValue(sh, s);
      if (v > bestHall) bestHall = v;
    }
    if (bestHall <= 0) continue;

    let worst = -1;
    let worstValue = 0;
    for (let i = 0; i < mine.slots.length; i++) {
      const v = slotValue(sh, mine.slots[i]);
      if (v <= 0) continue;
      if (worst < 0 || v < worstValue) { worst = i; worstValue = v; }
    }
    if (worst < 0) continue;
    if (bestHall * 10000 < worstValue * TRADE_UP_BP) continue;
    return {
      heroArmy: hero.army as string, townArmy: t.garrison as string,
      slot: worst, mustDismiss: hallFull, gain: bestHall - worstValue,
    };
  }
  return null;
}

function consolidate(
  sh: Shadow, p: DifficultyProfile, legal: readonly LegalCommand[],
): Candidate[] {
  const out: Candidate[] = [];
  const threatened = threatenedTownIds(sh, p);
  // Nothing to concentrate *for* yet. Until a rival *seat* has been found there
  // is no march to load an army onto, and the only thing the kingdom can lose
  // in the meantime is the hall the army is standing in - so an opening
  // capital keeps what the generator gave it. Measured on the ten-seed 36x36
  // headline: stripping it from day one put four of ten games back inside the
  // six-day window round 9 emptied.
  //
  // A seat is a town somebody else *sits in*, and the owner test is the whole
  // rule. `knownEnemies` publishes every town the viewer does not own, neutral
  // ones included - deliberately, because an unowned town is a target - so the
  // round the map gained neutral towns was the round this test started passing
  // on the first morning of every game. Measured on 36x36 seed 32676 with the
  // flag traced: `FOUND ... day=1 town=town:3 owner=null`, a neutral hall seven
  // cells from the capital, and the opening army was in one hand before anybody
  // had seen a rival. Four of the ten 36x36 seeds went back inside the six-day
  // window that way, which is exactly the failure the comment above describes
  // arriving through a door nobody had shut.
  //
  // `owner === null` is therefore not enough to unlock it. That covers both the
  // genuinely neutral hall and the remembered snapshot too stale to name an
  // owner; neither is a seat, and marching on either is a raid the kingdom can
  // do with the army it already carries.
  let found = false;
  for (const e of sh.enemies) {
    if (e.kind !== "town") continue;
    if (e.owner === null || (e.owner as string) === (sh.player as string)) continue;
    found = true;
    break;
  }

  const garrisonOf = new Set<string>();
  const keep = new Map<string, number>();
  for (const id of entityKeys(sh.state.towns)) {
    const t = sh.state.towns[id];
    if (t === undefined) continue;
    // Only a town with a hero standing in it can hand its garrison over.
    if (t.visitingHero === null) continue;
    if (threatened.has(t.id as string)) continue;
    if (!found) continue;
    garrisonOf.add(t.garrison as string);
    if (t.garrisonHero === null) keep.set(t.garrison as string, keptStack(sh, t.garrison));
  }
  const heroArmies = new Set<string>();
  const main = mainHero(sh);
  let mainArmy: ArmyId | null = null;
  for (const id of entityKeys(sh.state.heroes)) {
    const h = sh.state.heroes[id];
    if (h === undefined) continue;
    heroArmies.add(h.army as string);
    if (h.id === main) mainArmy = h.army;
  }

  const trade = tradeUp(sh, main as string | null, threatened);

  for (const c of legal) {
    if (c.type === "MERGE_STACK") {
      out.push({ command: c, score: 400, why: "merge" });
      continue;
    }
    if (c.type === "DISMISS_STACK") {
      if (trade === null || !trade.mustDismiss) continue;
      if ((c.army as string) !== trade.heroArmy || c.slot !== trade.slot) continue;
      out.push({ command: c, score: tradeScore(trade), why: "garrison.tradeup" });
      continue;
    }
    if (c.type !== "TRANSFER_STACK") continue;
    const from = c.fromArmy as string, to = c.toArmy as string;
    if (trade !== null && !trade.mustDismiss
      && from === trade.heroArmy && to === trade.townArmy && c.fromSlot === trade.slot) {
      out.push({ command: c, score: tradeScore(trade), why: "garrison.tradeup" });
      continue;
    }
    if (!garrisonOf.has(from) || !heroArmies.has(to)) continue;
    if (keep.get(from) === c.fromSlot) continue;
    // Biggest stack first, and priced where the movement scorer can see it.
    const stack = slotValue(sh, sh.state.armies[c.fromArmy]?.slots[c.fromSlot]);
    const toMain = mainArmy !== null && to === (mainArmy as string);
    if (!toMain) {
      out.push({ command: c, score: CONSOLIDATE_TIDY, why: "garrison.out" });
      continue;
    }
    out.push({
      command: c,
      score: Math.max(CONSOLIDATE_MAIN, stack * PICKUP_PER_VALUE),
      why: "garrison.out",
    });
  }
  return out;
}

export function bestTownCommand(
  sh: Shadow, p: DifficultyProfile, legal: readonly LegalCommand[],
): Candidate | null {
  const all = planTown(sh, p, legal);
  return all.length === 0 ? null : all[0]!;
}
